import { ROUTES } from '../../router/urls';
import { FundingTypeEnum } from '../fund-my-purchase/types';

export type ContractGuaranteeType = 'PERFORMANCE_BOND' | 'ADVANCED_PAYMENT_BOND';

export type WordingType = 'UNCONDITIONAL' | 'CONDITIONAL';

export type CollateralInstruction = 'CASH_MARGIN' | 'FACILITY' | 'FIXED_DEPOSIT';

export interface CollateralOption {
    value: CollateralInstruction;
    title: string;
    description: string;
}

export interface WordingOption {
    value: WordingType;
    label: string;
}

/**
 * Public Procurement Regulations 2025, reg 315(2): an advance payment may not
 * exceed thirty percent of the contract value; 315(3) allows more only with the
 * Authority's approval. The service refuses anything above this.
 */
export const ADVANCE_PAYMENT_CAP_PERCENT = 30;

/** Forms of security a procuring entity may accept. Reg 29(4). */
export const SECURITY_FORMS = [
    { value: 'BANK_GUARANTEE', label: 'Bank guarantee' },
    { value: 'LETTER_OF_CREDIT', label: 'Irrevocable letter of credit' },
    { value: 'SURETY_BOND', label: 'Surety or insurance bond' },
    { value: 'CASH', label: 'Cash, certified cheque or bank draft' },
    { value: 'SECURING_DECLARATION', label: 'Performance securing declaration' },
];

export const securityFormLabel = (value?: string): string => {
    const match = SECURITY_FORMS.filter((form) => form.value === value)[0];
    return match ? match.label : '—';
};

export interface ContractGuaranteeConfig {
    type: ContractGuaranteeType;
    /** Plural, used for page titles and breadcrumbs. */
    title: string;
    /** Singular, used in sentences and success copy. */
    singular: string;
    newLabel: string;
    referencePrefix: string;
    fundingType: FundingTypeEnum;
    listRoute: string;
    /**
     * Guarantees/Bonds is the single list in the sidebar; the per product lists
     * are reached through the create flow or a deep link. Navigation out of a
     * request returns here rather than to a list the user cannot otherwise find.
     */
    parentRoute: string;
    createRoute: string;
    viewRoute: string;
    bankQueueRoute: string;
    bankReviewRoute: string;
    steps: string[];
    beneficiaryHeading: string;
    beneficiarySearchLabel: string;
    beneficiaryPlaceholder: string;
    newBeneficiaryText: string;
    contractHeading: string;
    /** Label for the percentage the guarantee is struck at. */
    percentLabel: string;
    /** Label for the money figure derived from contract value x percent. */
    amountLabel: string;
    defaultPercent: string;
    /** Only advance payments carry a statutory ceiling. */
    maxPercent?: number;
    /** Purpose is a performance-guarantee-only concept. */
    purposes?: string[];
    wordingOptions: WordingOption[];
    collateralOptions: CollateralOption[];
    /** Shown above the terms step when the product has a precondition. */
    notice?: string;
    submittedNote: string;
    /** Advance payment guarantees track a balance that falls as deliveries land. */
    tracksOutstanding: boolean;
}

const collateralOptions: CollateralOption[] = [
    {
        value: 'CASH_MARGIN',
        title: '100% cash margin',
        description: 'Full guarantee value blocked on your account until expiry or release.',
    },
    {
        value: 'FACILITY',
        title: 'Existing guarantee facility',
        description: 'Utilise your approved bonds and guarantees line.',
    },
    {
        value: 'FIXED_DEPOSIT',
        title: 'Lien on fixed deposit',
        description: 'Pledge an existing term deposit as security.',
    },
];

export const PERFORMANCE_GUARANTEE: ContractGuaranteeConfig = {
    type: 'PERFORMANCE_BOND',
    title: 'Performance Guarantees',
    singular: 'Performance Guarantee',
    newLabel: 'New Performance Guarantee',
    referencePrefix: 'PG',
    fundingType: FundingTypeEnum.PERFORMANCE_BOND,
    listRoute: ROUTES.PERFORMANCE_GUARANTEES,
    parentRoute: ROUTES.BONDS,
    createRoute: ROUTES.NEW_PERFORMANCE_GUARANTEE,
    viewRoute: ROUTES.VIEW_PERFORMANCE_GUARANTEE,
    bankQueueRoute: ROUTES.BANK_PERFORMANCE_GUARANTEES,
    bankReviewRoute: ROUTES.BANK_REVIEW_PERFORMANCE_GUARANTEE,
    steps: ['Contract & Beneficiary', 'Guarantee Terms', 'Review & Issue'],
    beneficiaryHeading: 'Beneficiary Details',
    beneficiarySearchLabel: 'Search for beneficiary',
    beneficiaryPlaceholder: 'Select beneficiary',
    newBeneficiaryText: 'New Beneficiary',
    contractHeading: 'Contract & Guarantee Details',
    percentLabel: 'Guarantee %',
    amountLabel: 'Guarantee Amount',
    defaultPercent: '10',
    purposes: [
        'Performance security',
        'Advance payment guarantee',
        'Retention money guarantee',
        'Warranty / maintenance bond',
    ],
    wordingOptions: [
        { value: 'UNCONDITIONAL', label: 'Unconditional (on-demand)' },
        { value: 'CONDITIONAL', label: 'Conditional (default certificate required)' },
    ],
    collateralOptions,
    submittedNote:
        'Your performance guarantee request has been sent for review. Once approved and issued, it will appear here with a Download action.',
    tracksOutstanding: false,
};

export const ADVANCE_PAYMENT_GUARANTEE: ContractGuaranteeConfig = {
    type: 'ADVANCED_PAYMENT_BOND',
    title: 'Advance Payment Guarantees',
    singular: 'Advance Payment Guarantee',
    newLabel: 'New Advance Payment Guarantee',
    referencePrefix: 'APG',
    fundingType: FundingTypeEnum.ADVANCED_PAYMENT_BOND,
    listRoute: ROUTES.ADVANCE_PAYMENT_GUARANTEES,
    parentRoute: ROUTES.BONDS,
    createRoute: ROUTES.NEW_ADVANCE_PAYMENT_GUARANTEE,
    viewRoute: ROUTES.VIEW_ADVANCE_PAYMENT_GUARANTEE,
    bankQueueRoute: ROUTES.BANK_ADVANCE_PAYMENT_GUARANTEES,
    bankReviewRoute: ROUTES.BANK_REVIEW_ADVANCE_PAYMENT_GUARANTEE,
    steps: ['Buyer & Contract', 'Guarantee Terms', 'Review & Issue'],
    beneficiaryHeading: 'Buyer / Beneficiary Details',
    beneficiarySearchLabel: 'Search for buyer / beneficiary',
    beneficiaryPlaceholder: 'Select buyer / beneficiary',
    newBeneficiaryText: 'New Buyer',
    contractHeading: 'Contract & Advance Payment Details',
    percentLabel: 'Advance Payment %',
    amountLabel: 'Advance Payment Amount',
    defaultPercent: '20',
    maxPercent: ADVANCE_PAYMENT_CAP_PERCENT,
    wordingOptions: [
        { value: 'UNCONDITIONAL', label: 'Unconditional (on-demand)' },
        { value: 'CONDITIONAL', label: 'Conditional (proof of default required)' },
    ],
    collateralOptions,
    notice: 'This guarantee becomes effective only once the advance payment has been confirmed as received into the account below. The bank will verify this before issuance. The guarantee reduces as the advance is repaid, and expires once 80% of the contract price has been certified for payment.',
    submittedNote:
        'Your advance payment guarantee request has been sent for review. The bank will confirm receipt of the advance before issuing.',
    tracksOutstanding: true,
};

export const wordingLabel = (config: ContractGuaranteeConfig, value?: WordingType): string => {
    const match = config.wordingOptions.filter((option) => option.value === value)[0];
    return match ? match.label : '-';
};

export const collateralLabel = (config: ContractGuaranteeConfig, value?: CollateralInstruction): string => {
    const match = config.collateralOptions.filter((option) => option.value === value)[0];
    return match ? match.title : 'Not selected';
};
